import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/Button/Button';
import { AngleLeft } from '../../icons/AngleLeft';
import { PatientRecord, Section } from '../../shared/types/types';
import { PatientInfo } from './info/PatientInfo';
import './Infusion.scss';
import { PatientInfusion } from './infusion/PatientInfusion';
import { useCallback, useContext, useState, useEffect } from 'react';
import { fetchAuthenticated } from '../../controllers';
import { TAS_BACK_END_API_URL, URL_TAS_VERSION } from '../../constants';
import { decryptObject } from '../../components/Encryption/obfuscationHandler';
import { BannerMessageContext } from 'src/shared/contexts/BannerMessageContext';
import { v4 } from 'uuid';
import { InfusionNotes } from './infusion/InfusionNotes/InfusionNotes';
import { PatientCodeVerifyCard } from './infusion/patientCodeVerifyCard/patientCodeVerifyCard';
import { PatientBloodSamples } from './infusion/PatientBloodSamples/PatientBloodSamples';

export interface PatientProps {
  section: Section;
  token: string | null;
}

export interface PatientIdInfo {
  id?: string;
  token?: string;
  version?: string;
}

export interface PatientDataProps {
  patientIdInfo: PatientIdInfo;
}

export interface PatchPatientProps {
  patientIdInfo: PatientIdInfo;
  payload?: Partial<PatientRecord>;
}

export const Infusion = ({ section, token }: PatientProps) => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [patientInfo, setPatientInfo] = useState<PatientRecord>();
  const [patientInfoIsLoading, setPatientInfoIsLoading] = useState(false);
  const [patientInfoError, setPatientInfoError] = useState<any>();

  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string>('');
  const [imageString, setImageString] = useState<string>('');

  const [patientCodeVerified, setPatientCodeVerified] = useState<boolean>(false);

  const { addBannerMessage } = useContext(BannerMessageContext);

  const decryptAndSavePatientInfo = useCallback(
    async (encryptedPatientInfo: any, profilePhotoS3Url: string) => {
      const decryptedData = { ...encryptedPatientInfo };
      await decryptObject(decryptedData, token, null);
      setProfilePhotoUrl(profilePhotoS3Url);
      setPatientInfo(decryptedData);
    },
    [token]
  );

  const getPatientInfo = useCallback(
    async ({ patientIdInfo: { id: patientId } }: PatientDataProps) => {
      const url = `${TAS_BACK_END_API_URL}${URL_TAS_VERSION}/treatablepatients/${patientId}`;
      try {
        setPatientInfoIsLoading(true);
        const config: RequestInit = {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };
        const response = await fetchAuthenticated<any>(url, config);

        if (response?.ok) {
          if (response.parsedBody?.result) {
            let patientData = response.parsedBody.result.cas_data.length === 1 ? response.parsedBody.result?.cas_data[0] : response.parsedBody.result?.tds_data[0];
            patientData.patientDoc['obfuscationId'] = response.parsedBody.result.obfuscationId;
            await decryptAndSavePatientInfo(patientData?.patientDoc, patientData?.profilePhotoUrl);
          }
        }

        setPatientInfoIsLoading(false);
      } catch (e) {
        setPatientInfoIsLoading(false);
        setPatientInfoError(e);
        addBannerMessage({
          id: v4(),
          message: 'Uh oh! There was an error getting the patient data. Please refresh the page to try again.',
          type: 'error',
        });
      }
    },
    [decryptAndSavePatientInfo, addBannerMessage, token]
  );

  const getProfilePhotoUrl = useCallback(async (photoUrl: string) => {
    if (!photoUrl || photoUrl === '') return;
    try {
      await fetch(photoUrl)
        .then((response) => response.text())
        .then((result) => {
          setImageString(result);
        });
    } catch (error) {
      setImageString('');
    }
  }, []);

  useEffect(() => {
    if (!profilePhotoUrl || profilePhotoUrl === '') return;
    getProfilePhotoUrl(profilePhotoUrl);
  }, [profilePhotoUrl, getProfilePhotoUrl]);

  // get patient info when the component mounts

  useEffect(() => {
    getPatientInfo({ patientIdInfo: { id } });
  }, [getPatientInfo, id, token]);

  return (
    <div className="patient-container">
      <PatientInfo patientData={patientInfo} isLoading={patientInfoIsLoading} error={patientInfoError} profilePhotoString={imageString} token={token} />
      {patientCodeVerified ? (
        <div>
          <PatientInfusion patientData={patientInfo} section={section} token={token} />
          <PatientBloodSamples patientId={patientInfo?._id} medicalId={patientInfo?.encrypt_medicalId} token={token} />
        </div>
      ) : (
        <>
          <PatientCodeVerifyCard setPatientVerifyCard={setPatientCodeVerified} medicalId={patientInfo?.encrypt_medicalId} patientId={patientInfo?._id} token={token} />
        </>
      )}
      <InfusionNotes patientId={patientInfo?._id} token={token} />

      <div className="patient-controls">
        <Button
          label={'Back'}
          className={'go-back-button'}
          onClick={() => {
            navigate('/');
          }}
          icon={<AngleLeft />}
          iconPlacement={'left'}
        />
      </div>
    </div>
  );
};
